import Icon from "@components/ui/icon";
import Input from "@components/ui/input";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { go as fuzzysort } from "fuzzysort";
import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useOnClickOutside } from "usehooks-ts";

interface IProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (value: string) => void;
  isProcessing?: boolean;

  storageKey: string;
  amountOfStoredSearches?: number;

  className?: string;
  inputClassName?: string;
  placeholder?: string;

  onClear?: () => void;
}

export const SearchInputWithRecent = ({
  value,
  onChange,
  onSubmit,
  isProcessing,
  storageKey,
  amountOfStoredSearches = 3,
  className,
  inputClassName,
  placeholder,
  onClear,
}: IProps) => {
  "use no memo";
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const getRecentSearches = useCallback((): string[] => {
    try {
      const item = window.sessionStorage.getItem(storageKey);
      if (!item) return [];
      const parsed = JSON.parse(item);
      if (!Array.isArray(parsed)) return [];
      return parsed.slice(0, amountOfStoredSearches);
    } catch (e) {}

    return [];
  }, [storageKey, amountOfStoredSearches]);

  const [recentSearches, setRecentSearches] = useState(() => getRecentSearches());
  const [selectedSearch, setSelectedSearch] = useState(-1);

  useOnClickOutside(containerRef, () => {
    setIsFocused(false);
  });

  useEffect(() => {
    if (isFocused) return;

    const query = value.trim();
    if (query.length === 0) {
      return;
    }

    const tmp = [...recentSearches];
    const itemIdx = tmp.findIndex((s) => s === query);
    if (itemIdx >= 0) {
      tmp.splice(itemIdx, 1);
    }
    tmp.unshift(query);
    setRecentSearches(tmp.slice(0, amountOfStoredSearches));
    window.sessionStorage.setItem(storageKey, JSON.stringify(tmp));
  }, [isFocused, storageKey, amountOfStoredSearches]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      e.currentTarget.blur();
      setIsFocused(false);
      return;
    }

    const isArrowDown = e.key === "ArrowDown";
    const isArrowUp = e.key === "ArrowUp";
    if (isArrowUp || isArrowDown) {
      e.preventDefault();
      setSelectedSearch((prev) => {
        const newVal = prev + (isArrowDown ? 1 : -1);

        if (newVal > filteredRecent.length - 1) {
          return 0;
        }
        if (newVal < 0) {
          return filteredRecent.length - 1;
        }

        return newVal;
      });
      return;
    }

    if (e.key === "Enter") {
      e.currentTarget.blur();
      if (filteredRecent.length === 0) return;

      if (selectedSearch < 0) {
        onSubmit(filteredRecent[0]);
        setIsFocused(false);
      }

      const query = filteredRecent[selectedSearch];
      if (query) {
        onSubmit(query);
        setIsFocused(false);
      }

      return;
    }
  };

  const filteredRecent = value.length === 0
    ? recentSearches
    : fuzzysort(value, recentSearches).map((o) => o.target);

  return (
    <div className={cn("tw-relative", className)} ref={containerRef}>
      <Input
        type="text"
        ref={inputRef}
        autoFocus={false}
        tabIndex={-1}
        className={cn(
          inputClassName,
          "tw-h-10 !tw-rounded-md tw-py-1.5 tw-pl-9 tw-pr-2 tw-text-[13px]/normal",
        )}
        iconClassName="tw-left-2"
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        icon="ri-search-line"
        value={value}
        onChange={(e) => {
          onChange(e);
          setSelectedSearch(-1);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          onSubmit(value);
        }}
      />
      {isProcessing ? (
        <div className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2">
          <Spinner />
        </div>
      ) : (
        onClear &&
        value.length > 0 &&
        !isFocused && (
          <button
            type="button"
            className="tw-absolute tw-right-3 tw-top-1/2 tw-grid -tw-translate-y-1/2 tw-items-center tw-outline-none"
            onClick={() => onClear()}
          >
            <Icon
              icon="ri-close-line"
              className="tw-text-[24px]/none tw-text-blue-500"
            />
          </button>
        )
      )}

      {isFocused && filteredRecent.length > 0 && (
        <div
          data-rich-text-focus-ignore="true"
          className="tw-absolute tw-left-0 tw-top-full tw-z-10 tw-mt-2 tw-w-full tw-rounded-lg tw-border tw-border-blue-100 tw-bg-white tw-p-2 tw-shadow-popover"
        >
          <span className="tw-pl-3 tw-text-[11px] tw-font-medium">Recent</span>
          <ol className="tw-m-0 tw-mt-2 tw-grid tw-list-none tw-content-start tw-gap-1">
            {filteredRecent.map((search, idx) => (
              <li
                key={search}
                role="option"
                aria-selected={selectedSearch === idx}
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!inputRef.current) {
                    return;
                  }
                  onSubmit(search);
                  inputRef.current.blur();
                  setIsFocused(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    e.preventDefault();
                    if (!inputRef.current) return;
                    onSubmit(search);
                    inputRef.current.blur();
                    setIsFocused(false);
                  }
                }}
                className={cn({
                  "group/item tw-flex tw-cursor-pointer tw-items-center tw-rounded-md tw-px-3 tw-py-2 hover:tw-bg-blue-50": true,
                  "tw-bg-blue-50": selectedSearch === idx,
                })}
              >
                <Icon
                  icon="ri-time-line"
                  className={cn({
                    "tw-mr-2 tw-text-[16px]/none tw-text-blue-500 group-hover:tw-text-primary": true,
                    "tw-text-primary": selectedSearch === idx,
                  })}
                />
                <span
                  className={cn({
                    "tw-text-[11px] group-hover:tw-text-primary": true,
                    "tw-text-primary": selectedSearch === idx,
                  })}
                >
                  {search}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};
